import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";
import { SaintComparisonStatBand } from "@/components/SaintComparisonInline";

const faqs: FAQItem[] = [
  {
    question:
      "What treatment should I have ready when my boss asks 'what would I even do?'",
    answer:
      "SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy). It's an FDA-cleared, accelerated form of TMS developed at Stanford. It takes five days, not six weeks, and in clinical trials produced remission in 79 percent of people whose depression hadn't responded to medication, with an average time to remission of 2.6 days. No anesthesia, nothing in the bloodstream, no interaction with other medications. It's the treatment that fits an executive's life because the entire course is one blocked week.",
  },
  {
    question: "Can I schedule treatment on my executive's behalf?",
    answer:
      "No. The consultation is their decision and their call to make. You can call Neuro Wellness at 954-827-9793 to ask questions and understand the process, but you should not schedule treatment without their explicit request. Your role is to gather information and offer to set it up — not to do it unasked.",
  },
  {
    question: "How do I explain SAINT in 30 seconds?",
    answer:
      "\"There's a treatment called SAINT. It's from Stanford, FDA-cleared, and it's for depression that hasn't responded to medication. It's five days, not six weeks, no drugs, no anesthesia, and in trials nearly eight in ten people went into full remission. There's a clinic that does it, Neuro Wellness. I've already talked to them. The first appointment is just an evaluation. If you want, I'll put it on the calendar and call it whatever you like.\"",
  },
  {
    question: "What exactly can I do to help beyond the conversation?",
    answer:
      "Call Neuro Wellness yourself to understand the process. Prepare the calendar cover — block the consultation and treatment week under whatever label they choose. Assemble their medication history if they're willing. Handle logistics for the week — transportation, meals, a quiet place to work between sessions. Then step back. Once they're in care, your role returns to what it was.",
  },
];

const related: RelatedLink[] = [
  {
    title:
      "You're the Executive Assistant. You're Watching Your Boss Fall Apart. Here's What You're Actually Seeing.",
    href: "/youre-the-executive-assistant-watching-your-boss-fall-apart",
    description:
      "The first article: understanding the clinical pattern behind what you're seeing.",
  },
  {
    title:
      "How to Tell Your Boss You're Worried About Them (Without Losing Your Job or Their Trust)",
    href: "/how-to-tell-your-boss-youre-worried",
    description:
      "The second article: what to say, what not to say, and how to raise it without overstepping.",
  },
  {
    title: "The Executive Who Can't Afford Six Weeks",
    href: "/the-executive-who-cant-afford-six-weeks",
    description:
      "Why the highest-functioning patients end up with the lowest-yield treatment — and what changes the calculation.",
  },
  {
    title: "How to Do Due Diligence on a Depression Treatment",
    href: "/how-to-do-due-diligence-on-a-depression-treatment",
    description:
      "An eight-question framework for evaluating any depression treatment — using SAINT as the worked example.",
  },
];

export default function WhatToHaveReadyResourceGuide() {
  return (
    <DepressionContentPage
      seo={{
        title:
          "What to Have Ready: A Resource Guide for the Executive Assistant",
        description:
          "The practical guide for when your boss asks 'what would I even do?' The treatment options, which one fits an executive's life, and exactly how to make the first step happen — including SAINT explained in terms you can repeat.",
        canonical: "/what-to-have-ready-resource-guide-executive-assistant",
        keywords:
          "executive assistant resource guide, SAINT TMS, executive depression treatment, FDA-cleared depression treatment, five-day depression treatment, Stanford neuromodulation, treatment-resistant depression, executive assistant boss depression resource",
      }}
      badge="For Executive Assistants"
      heroTitle='What to Have Ready: A Resource Guide for the Executive Assistant Who&apos;s Been Asked "What Would I Even Do?"'
      heroSubtitle="They said yes. Or something close to it. The door is open, and if you don't have something ready to put through it, it will close again. This is the practical part: what the options are, which one fits an executive's life, and exactly how to make the first step happen."
      lastUpdated="2026-11-03"
      sections={[
        {
          body: (
            <div className="space-y-5">
              <p>
                They said yes. Or something close to it. Maybe it was a full
                conversation; maybe it was "I don't know, what would I even do?"
                said quietly at the end of a bad day. Either way, the door is
                open, and if you don't have something ready to put through it,
                it will close again.
              </p>
              <p>
                This is the third article in a series for executive assistants
                supporting a leader who has been through trauma and is now
                caught in depression and anxiety. The first was about
                recognizing what you're seeing. The second was about how to
                raise it. This one is the practical part: what the options are,
                which one fits an executive's life, and exactly how to make the
                first step happen.
              </p>
            </div>
          ),
        },
        {
          heading: "What you're likely dealing with",
          body: (
            <div className="space-y-5">
              <p>
                Trauma followed by a persistent, worsening spiral of low mood,
                anxiety, exhaustion, and impaired decision-making is,
                clinically, very often major depressive disorder with anxious
                features. Not grief anymore. Not burnout. A condition with
                treatments.
              </p>
              <p>
                Your executive may already have seen someone. Many have. Many
                are quietly on an antidepressant that took the edge off and
                nothing more, or that they stopped because of side effects. If
                that's the case, they're in a specific category: depression that
                hasn't responded adequately to medication. That category has its
                own solutions, and they're better than most people know.
              </p>
            </div>
          ),
        },
        {
          heading: "The options, in plain terms",
          body: (
            <div className="space-y-4">
              <p>
                <strong>Therapy.</strong> Essential, and it works. But it's
                slow, and for someone in an acute spiral, it's often not enough
                on its own. If they're not seeing anyone, this should start. If
                they are, it should continue.
              </p>
              <p>
                <strong>Antidepressant medication.</strong> The default first
                step. Six to twelve weeks to judge each one. Systemic side
                effects. For someone who's already tried one without adequate
                relief, the odds that the next one produces remission are around
                14 percent.
              </p>
              <p>
                <strong>Esketamine.</strong> A ketamine-derived nasal spray,
                clinic-administered with monitoring. Works quickly for some, but
                typically requires ongoing maintenance sessions, and involves
                dissociation during treatment. Hard to make discreet.
              </p>
              <p>
                <strong>ECT.</strong> The most effective conventional option for
                severe depression, with a remission rate around 48 percent.
                Requires general anesthesia, multiple sessions a week for
                several weeks, and carries a risk of memory problems. Rarely a
                first choice for a working executive.
              </p>
              <p>
                <strong>Standard TMS.</strong> Magnetic stimulation of the
                brain's mood-regulation region. No anesthesia, no drugs. But it
                requires one visit a day, five days a week, for six to nine
                weeks: 30 to 36 appointments. For an executive's calendar,
                that's usually the reason it doesn't happen.
              </p>
              <p>
                <strong>SAINT.</strong> The one you should understand well
                enough to explain.
              </p>
            </div>
          ),
        },
        {
          heading: "SAINT, explained for someone who has to explain it",
          body: (
            <div className="space-y-4">
              <p>
                SAINT stands for Stanford Accelerated Intelligent
                Neuromodulation Therapy. It was developed at Stanford's Brain
                Stimulation Lab, received FDA Breakthrough Device Designation,
                and was cleared in 2022 for adults with major depressive
                disorder who haven't gotten adequate relief from antidepressants
                in their current episode.
              </p>
              <p>Here's why it fits an executive:</p>
              <ul className="list-disc pl-5 space-y-3">
                <li>
                  <strong>It's five days.</strong> Ten 10-minute sessions per
                  day, with 50-minute breaks between them, for five consecutive
                  days. That's one blocked week, not two months of daily
                  appointments. Between sessions, they can work, take calls, or
                  rest.
                </li>
                <li>
                  <strong>It's precise.</strong> Before treatment, a 45-minute
                  functional MRI maps their individual brain. An algorithm
                  identifies the exact spot to target. Neuronavigation keeps the
                  treatment coil on that spot, every session. It's the only
                  FDA-cleared depression treatment that plans care from an image
                  of the patient's own brain.
                </li>
                <li>
                  <strong>It's clean.</strong> No anesthesia. Nothing enters the
                  bloodstream. No interaction with other medications, which
                  don't need to be stopped. The most common side effects in
                  trials were mild headache, fatigue, and temporary scalp
                  discomfort.
                </li>
                <li>
                  <strong>It works.</strong> In the clinical trials behind its
                  clearance, 79 percent of participants, all of whom had failed
                  prior treatment, reached remission. Average time to remission:
                  2.6 days. Across more than 400 trial patients, 85 percent had
                  symptom relief, with no serious adverse events.
                </li>
                <li>
                  <strong>It's discreet.</strong> Delivered at a private
                  outpatient clinic. A week away can be called anything the
                  executive wants it called.
                </li>
              </ul>
              <p>
                It isn't appropriate for people with non-removable metal in or
                near the head or an implanted device, which is screened at
                consultation.
              </p>
            </div>
          ),
        },
        {
          heading: "How the process actually works",
          body: (
            <div className="space-y-5">
              <p>
                At a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Provider
                </Link>{" "}
                like Neuro Wellness TMS Centers of America:
              </p>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Consultation.</strong> The executive meets with the
                  clinical team, reviews their history, and finds out whether
                  they're a candidate. One appointment. No commitment. The
                  initial consultation is a Zoom call; as steps evolve, they'll
                  also have the option to meet by Zoom web conference or in
                  person. The mandatory in-person steps include fMRI mapping and
                  the actual treatment.
                </li>
                <li>
                  <strong>fMRI.</strong> Roughly 45 minutes.
                </li>
                <li>
                  <strong>Targeting.</strong> Done behind the scenes; nothing
                  required of the patient.
                </li>
                <li>
                  <strong>Treatment week.</strong> Five consecutive days,
                  roughly full days at the clinic. Weekend availability exists
                  for schedules that need it.
                </li>
              </ol>

              <SaintComparisonStatBand />
            </div>
          ),
        },
        {
          heading: "Exactly what you can do",
          body: (
            <div className="space-y-5">
              <p>
                <strong>Call first, yourself.</strong> You can phone Neuro
                Wellness at 954-827-9793 to ask questions on your executive's
                behalf: what the consultation involves, how scheduling works,
                what the week looks like, what insurance or self-pay options
                exist. You don't need to give their name. You're gathering
                information so that you can explain it in thirty seconds when
                the moment comes.
              </p>
              <p>
                <strong>Don't schedule treatment for them.</strong> The
                consultation is their decision and their call to make, or one
                they explicitly ask you to make on their behalf. Offer to set it
                up. Don't do it unasked.
              </p>
              <p>
                <strong>Prepare the calendar cover.</strong> If they say yes,
                block the consultation and, later, the treatment week under
                whatever label they choose. "Offsite." "Personal." "Medical."
                Your discretion is the whole reason they can do this.
              </p>
              <p>
                <strong>Assemble the history.</strong> If they're willing, help
                them list current and past medications, doses, and dates, and
                the names of any clinicians they've seen. The consultation will
                ask for this, and executives rarely have it organized.
              </p>
              <p>
                <strong>Handle the logistics of the week.</strong>{" "}
                Transportation, meals, a quiet place to work between sessions if
                they want one, a plan for what gets delegated and what waits.
              </p>
              <p>
                <strong>Then step back.</strong> Once they're in care, your role
                returns to what it was. You've done the part only you could do.
              </p>
            </div>
          ),
        },
        {
          heading: "What to say when they ask",
          body: (
            <div className="space-y-5">
              <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground">
                "There's a treatment called SAINT. It's from Stanford,
                FDA-cleared, and it's for depression that hasn't responded to
                medication. It's five days, not six weeks, no drugs, no
                anesthesia, and in trials nearly eight in ten people went into
                full remission. There's a clinic that does it, Neuro Wellness.
                I've already talked to them. The first appointment is just an
                evaluation. If you want, I'll put it on the calendar and call it
                whatever you like."
              </blockquote>
            </div>
          ),
        },
        {
          heading: "What to do next",
          body: (
            <div className="space-y-4">
              <p className="font-semibold text-primary">
                Schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p>
                Learn more:{" "}
                <Link
                  to="/saint"
                  className="text-primary font-semibold hover:underline"
                >
                  neurowellnesstms.com/saint
                </Link>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                If your executive says anything that suggests they're thinking
                about ending their life, treat it as an emergency. Encourage
                them to call or text 988 (Suicide &amp; Crisis Lifeline), or
                call it yourself for guidance on how to help. This article is
                for educational purposes and is not a substitute for individual
                medical advice.
              </p>
            </div>
          ),
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="Schedule a Consultation"
    />
  );
}
